import { useEffect, useState } from "react";
import { useDraft } from "../../hooks/useDraft";
import { DraftRefusedError, type DraftRefusal, useSubmitApproval } from "../../hooks/useQueues";
import { useSettleGuard } from "../../hooks/useSettleGuard";
import {
  JUDGED_DEFERRAL_HINT,
  REPLACED_BY_NEW_ANSWER_HINT,
  isJudgedTargetDeferral,
} from "../../lib/exploratoryReading";
import { rootNameOf } from "../../lib/displayName";
import { stripAnchors } from "../../lib/stripAnchors";
import { KEY_HINT_INLINE, shortcut } from "../../lib/platformKeys";
import { provenanceLines, settlesLine, type ReviewTreeMeta } from "../../lib/reviewLines";
import { DEPENDENTS_HEADING, nodeTitle } from "../../lib/specLines";
import { DetailsFold } from "./DetailsFold";
import { NavFooter, asString, cardKeyAct, isTypingTarget } from "./shared";
import type { components } from "../../api/types";

type Node = components["schemas"]["Node"];

// ---- Decision panel -------------------------------------------------

export function DecisionPanel({
  productId,
  node,
  nodeById,
  pos,
  onPrev,
  onNext,
  onActNext = onNext,
  replacedByNewAnswer = false,
  mountedByDataSwap = false,
  onRefused = () => {},
  treeMeta,
  actsHeld = false,
  nothingWaits = false,
  dependents = [],
}: {
  productId: string;
  node: Node;
  nodeById: Map<string, Node>;
  pos: number;
  onPrev: () => void;
  // The founder's own forward move (skip →, the right arrow).
  onNext: () => void;
  // 0051 S1c: the forward move an act causes - Confirm's and Reject's success
  // path calls it, so the page knows the move was an act's (its keyboard
  // guard holds the next card's key acts). Default onNext: a card rendered
  // without the page's act-advance moves on as its own forward move does.
  onActNext?: () => void;
  // 0157-15, founder ruling Q8: the SERVER's list - node.id is in the decisions
  // queue's served replaced_by_new_answer_node_ids (a saved changed answer will
  // replace this proposed decision when the founder runs). Never inferred here.
  replacedByNewAnswer?: boolean;
  // 0148 A40: this panel was mounted because the anchored node LEFT the queue.
  mountedByDataSwap?: boolean;
  // 0159 F2: a Confirm or Reject the server refused (409 draft.stale: the
  // decision is no longer proposed) shows its line in the Review page's notice.
  // It is the submit hook's onRefused option, this decision its item (so the
  // notice carries its id and title), so it lands even when this panel
  // unmounted before the 409 arrived; a refused act does not advance, and the
  // queue refetch the refusal triggers drops the decision, so the panel that
  // slides in is held (A40).
  onRefused?: (refusal: DraftRefusal) => void;
  // 0051 S1a, S1c: the served meta the "Settles:" line reads and the product's
  // name its anchors to the root read as.
  treeMeta?: ReviewTreeMeta;
  // 0051 S1a (F3-2): the page's mount gate; holds Confirm, Reject and their
  // keystrokes (see QuestionPanel).
  actsHeld?: boolean;
  // 0051 S1c: nothing waits on the founder in either queue (the page's forward
  // rule). Default false: a card rendered without the page's served sets never
  // reads "done".
  nothingWaits?: boolean;
  // 0051 slice 3 (approval-moments-7): the served ids of the current
  // decisions and waiting questions that cite this decision (planner's one
  // dependents rule), the card's "what builds on this".
  dependents?: readonly string[];
}) {
  const fields = (node.fields ?? {}) as Record<string, unknown>;
  const submit = useSubmitApproval(productId, { item: node, onRefused });
  // 0148 A40: a panel a data swap mounted holds its keystroke ACTS (the y / n
  // shortcuts) until the founder clicks inside it or navigates - the founder
  // never chose this decision, and a stray y would confirm it unread. Captured
  // once at mount; see QuestionPanel.
  const [keyActsHeld, setKeyActsHeld] = useState(mountedByDataSwap);
  // 0074, 0051 S1c: Confirm approves in one tap, so after a move an act caused
  // shows this card it takes no tap or key for the settle time, and it never
  // takes the click that ends a press it did not see begin (the founder's
  // ruling of 2026-10-19); after the founder's own navigation it is not held.
  const confirmGuard = useSettleGuard({ afterActMove: true });

  const [reasonOpen, setReasonOpen] = useState(false);
  // Skip the third tuple slot (`clear`) — see the QuestionPanel
  // comment for the same pattern. `commit` writes the rejection
  // reason synchronously on save.
  const [reason, setReason, , commit] = useDraft(productId, `${node.id}-reject-reason`, "");

  // Real decision fields: fields.summary OR fields.value carries the
  // proposal body; fields.reasoning_chain carries the rationale. Both
  // optional in the schema; render only what's present.
  // 0051 S1a: the summary leads, and the full value - the text that becomes
  // canonical - always shows under Proposed (a summary-only decision shows
  // its summary there).
  const rootName = rootNameOf(treeMeta);
  const summary = stripAnchors(asString(fields.summary), rootName);
  const value = stripAnchors(asString(fields.value), rootName);
  const body = value || summary;
  const lead =
    value !== "" && summary !== "" && summary !== value && summary !== node.title ? summary : "";
  const reasoning = stripAnchors(asString(fields.reasoning_chain), rootName);
  // Where the proposal comes from, what it replaces, what it settles (the
  // allowlist), and what it builds on (under Details).
  const provenance = provenanceLines(node, nodeById);
  const settles = settlesLine(node, treeMeta);
  const sourceLines = [...provenance.fromAnswer, ...provenance.replaces, ...(settles !== "" ? [settles] : [])];
  // What builds on it, by title (never a raw id: an id the served context does
  // not hold, or a blank title, is left out).
  const dependentTitles = dependents
    .map((id) => nodeById.get(id))
    .filter((n): n is Node => n !== undefined)
    .map((n) => nodeTitle(n, nodeById))
    .filter((t) => t !== "");

  const trimmedReason = reason.trim();

  const confirm = () => {
    if (actsHeld) return;
    if (submit.isPending) return;
    submit.mutate(
      { decisionId: node.id, action: "confirm" },
      {
        onSuccess: () => {
          setReasonOpen(false);
          // No text to commit on confirm; localStorage stays as-is
          // (whatever the founder typed in the reject-reason field
          // before deciding to confirm). Harmless: when they next
          // visit, the reject-reason draft is preloaded only when
          // they reopen the reject path.
          onActNext();
        },
      },
    );
  };

  const reject = () => {
    if (actsHeld) return;
    if (submit.isPending) return;
    if (!reasonOpen) {
      setReasonOpen(true);
      return;
    }
    if (trimmedReason.length === 0) return;
    submit.mutate(
      { decisionId: node.id, action: "reject", rejectionReason: trimmedReason },
      {
        onSuccess: () => {
          setReasonOpen(false);
          // Mirror the posted reason into localStorage synchronously
          // so the reject-reason field round-trips on Previous.
          commit(trimmedReason);
          onActNext();
        },
      },
    );
  };

  // Y / N shortcuts when no input has focus; ←/→ navigate same way.
  // Cmd/Ctrl+Enter inside the reject reason submits it. The acts are read
  // through cardKeyAct (shared.tsx), the one spelling Review's keyboard guard
  // reads too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const inText = isTypingTarget(e);
      const keyAct = cardKeyAct(e);
      if (keyAct === "confirm") {
        e.preventDefault();
        // 0148 A40: held on a data-swapped panel until a click inside it.
        // 0051 S1a: and while the queue's mount fetch is still out.
        if (!keyActsHeld && !actsHeld) confirm();
      } else if (keyAct === "reject") {
        e.preventDefault();
        if (!keyActsHeld && !actsHeld) reject();
      } else if (!inText && e.key === "ArrowRight") {
        e.preventDefault();
        onNext();
      } else if (!inText && e.key === "ArrowLeft" && pos > 0) {
        e.preventDefault();
        onPrev();
      } else if (keyAct === "submit" && reasonOpen) {
        e.preventDefault();
        if (!actsHeld) reject();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div className="animate-slide-up" onClickCapture={() => setKeyActsHeld(false)}>
      <h1 className="text-2xl font-medium text-ink-50 tracking-tighter2 leading-snug">
        {node.title}
      </h1>

      {lead !== "" && (
        <p
          data-testid="decision-lead"
          className="mt-4 break-words text-[15px] text-ink-300 leading-relaxed max-w-2xl whitespace-pre-wrap"
        >
          {lead}
        </p>
      )}

      {body !== "" && body !== node.title && (
        <div className="mt-6">
          <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-2">
            Proposed
          </p>
          <p className="break-words text-[15px] text-ink-200 leading-[1.7] max-w-2xl whitespace-pre-wrap">
            {body}
          </p>
        </div>
      )}

      {/* 0051 S1a: the source lines - which answer it came from, what it
          replaces, what it settles. Never a trade-off or flip line the data
          does not carry. */}
      {sourceLines.length > 0 && (
        <ul
          data-testid="decision-source-lines"
          className="mt-4 space-y-1 break-words text-[13px] text-ink-300 max-w-2xl"
        >
          {sourceLines.map((line, i) => (
            <li key={`${i}-${line}`}>{line}</li>
          ))}
        </ul>
      )}

      {/* 0157 Q3 (shortened by Q12): a proposed decision minted from the part
          of the founder's own answer the judge read as held back. A side
          deferral, a non-proposed decision and every other decision show
          nothing; founder ruling Q15: a card the served list below names shows
          only the replace line. */}
      {node.status === "proposed" && isJudgedTargetDeferral(fields) && !replacedByNewAnswer && (
        <p data-testid="judged-deferral-hint" className="mt-4 break-words text-[13px] text-ink-300 max-w-2xl">
          {JUDGED_DEFERRAL_HINT}
        </p>
      )}

      {/* 0157 Q8: a decision the decisions queue serves as replaced by a saved
          changed answer, target and side cards alike (the list decides). */}
      {replacedByNewAnswer && (
        <p data-testid="replaced-by-new-answer-hint" className="mt-4 break-words text-[13px] text-ink-300 max-w-2xl">
          {REPLACED_BY_NEW_ANSWER_HINT}
        </p>
      )}

      <div className="mt-10 flex items-center gap-3">
        <button
          type="button"
          onClick={() => confirmGuard.onClick(confirm)}
          onPointerDown={confirmGuard.onPointerDown}
          onPointerUp={confirmGuard.onPointerUp}
          onPointerCancel={confirmGuard.onPointerCancel}
          onKeyDown={confirmGuard.onKeyDown}
          disabled={submit.isPending || actsHeld}
          className="inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-ink-950 hover:bg-accent-glow transition-colors disabled:cursor-not-allowed disabled:opacity-40"
        >
          {submit.isPending && submit.variables?.action === "confirm" ? (
            "saving…"
          ) : (
            <>
              Confirm
              <span className={`font-mono text-[11px] opacity-50 ${KEY_HINT_INLINE}`}>[ Y ]</span>
            </>
          )}
        </button>
        <button
          type="button"
          onClick={reject}
          disabled={submit.isPending || actsHeld}
          className="inline-flex items-center gap-2 rounded-md border border-ink-700 hover:border-ink-500 px-4 py-2 text-sm font-medium text-ink-100 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
        >
          {submit.isPending && submit.variables?.action === "reject" ? (
            "saving…"
          ) : (
            <>
              Reject
              <span className={`font-mono text-[11px] text-ink-400 opacity-70 ${KEY_HINT_INLINE}`}>[ N ]</span>
            </>
          )}
        </button>
      </div>

      {reasonOpen && (
        <div className="mt-6 animate-fade-in">
          <label className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-2 block">
            Why are you rejecting?
          </label>
          <div
            className={`rounded-lg border ${
              submit.isPending
                ? "border-ink-700"
                : "border-ink-700 hover:border-ink-600 focus-within:border-danger"
            } bg-ink-900/60 transition-colors`}
          >
            <textarea
              autoFocus
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submit.isPending}
              placeholder="A short reason. We use it to propose something better."
              className="w-full resize-none bg-transparent px-5 py-3 text-sm text-ink-100 placeholder:text-ink-500 focus:outline-none disabled:opacity-60"
            />
            <div className="flex items-center justify-between gap-3 border-t border-ink-800 px-3 py-2">
              <span className={`font-mono text-[11px] text-ink-400 ${KEY_HINT_INLINE}`}>
                {shortcut("↵")} to reject
              </span>
              <button
                type="button"
                onClick={reject}
                disabled={submit.isPending || trimmedReason.length === 0 || actsHeld}
                className="ml-auto rounded-md bg-danger-solid/80 hover:bg-danger-solid px-3 py-1 text-xs font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                {submit.isPending ? "rejecting…" : "Reject with reason"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 0051 S1a: the reasoning and what the decision builds on, folded. */}
      {(reasoning !== "" || provenance.buildsOn.length > 0 || dependentTitles.length > 0) && (
        <DetailsFold>
          {reasoning !== "" && (
            <div className="border-l-2 border-ink-800 pl-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-1">
                Reasoning
              </p>
              <p className="break-words text-[14px] text-ink-300 leading-relaxed whitespace-pre-wrap">
                {reasoning}
              </p>
            </div>
          )}
          {provenance.buildsOn.length > 0 && (
            <ul data-testid="review-provenance" className="space-y-1 break-words text-[13px] text-ink-300">
              {provenance.buildsOn.map((line, i) => (
                <li key={`${i}-${line}`}>{line}</li>
              ))}
            </ul>
          )}
          {dependentTitles.length > 0 && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-1">
                {DEPENDENTS_HEADING}
              </p>
              <ul data-testid="review-dependents" className="space-y-1 break-words text-[13px] text-ink-300">
                {dependentTitles.map((title, i) => (
                  <li key={`${i}-${title}`}>{title}</li>
                ))}
              </ul>
            </div>
          )}
        </DetailsFold>
      )}

      {submit.error && !(submit.error instanceof DraftRefusedError) && (
        <p className="mt-4 text-sm text-danger">
          Couldn't save: {(submit.error as Error).message}
        </p>
      )}

      <NavFooter pos={pos} nothingWaits={nothingWaits} onPrev={onPrev} onNext={onNext} />
    </div>
  );
}
